import React from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Link,
  Divider,
  useTheme,
} from "@mui/material";

const Footer: React.FC = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  // Keep only these 3 pages
  const footerLinks = [
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms of Service", href: "/terms-of-service" },
    { label: "Contact Us", href: "/contact-us" },
  ];

  const shopkabotSite = "https://shopkabot.com";

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        marginTop: "auto",
        py: { xs: 2.5, sm: 4 },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={3}>
          {/* Main Content */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 3, sm: 6 }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "flex-start" }}
          >
            {/* Brand Section */}
            <Box sx={{ minWidth: { xs: "100%", sm: "280px" }, flex: 0 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.success.main})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 1,
                }}
              >
                Shopkabot
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5, textAlign: "left" }}
              >
                Shopkabot helps businesses automate WhatsApp sales, support,
                bookings, and payments with intelligent chat flows.
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Website:{" "}
                <Link
                  href={shopkabotSite}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  shopkabot.com
                </Link>
              </Typography>
            </Box>

            {/* Links Section */}
            <Box sx={{ minWidth: "160px", flex: 0 }}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ mb: 2, textAlign: "left" }}
              >
                Quick Links
              </Typography>
              <Stack spacing={1}>
                {footerLinks.map((link) => (
                  <Link
                    key={link.label}
                    href={link.href}
                    color="text.secondary"
                    underline="none"
                    sx={{
                      fontSize: "0.875rem",
                      transition: "color 0.2s ease",
                      textAlign: "left",
                      display: "block",
                      "&:hover": { color: theme.palette.primary.main },
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </Stack>
            </Box>
          </Stack>

          <Divider />

          {/* Bottom Section */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: "left" }}
            >
              &copy; {currentYear} Shopkabot. All rights reserved.
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: { xs: "left", sm: "right" } }}
            >
              Powered by shopkabot.com — building smarter WhatsApp commerce.
            </Typography>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;
